// Overlays: command palette, IAS legend modal, demo-mode banner.

import React, { useState, useEffect } from "react";
import { MOCK } from "./data.jsx";

export function CommandPalette({ onClose, setRoute, setRole, setDemoOn }) {
  const [q, setQ] = React.useState("");
  const [sel, setSel] = React.useState(0);

  const items = [
    { group: "Navigate", label: "Dashboard",        act: ()=>setRoute("dashboard"),  kb: "G D" },
    { group: "Navigate", label: "Pipeline",         act: ()=>setRoute("pipeline"),   kb: "G P" },
    { group: "Navigate", label: "Analysis view",    act: ()=>setRoute("results"),    kb: "G A" },
    { group: "Navigate", label: "New analysis",     act: ()=>setRoute("analyzer"),   kb: "G N" },
    { group: "Navigate", label: "Library",          act: ()=>setRoute("library")     },
    { group: "Navigate", label: "Admin · Users",    act: ()=>setRoute("admin-users") },
    { group: "Navigate", label: "Admin · Analytics", act: ()=>setRoute("admin-analytics") },
    { group: "Companies", label: "Helix Therapeutics — IAS 82", act: ()=>setRoute("results") },
    { group: "Companies", label: "Orbis Diagnostics — IAS 74",  act: ()=>setRoute("results") },
    { group: "Companies", label: "Kiva Biosystems — IAS 71",    act: ()=>setRoute("results") },
    { group: "Companies", label: "Sentrix Imaging — IAS 76",    act: ()=>setRoute("results") },
    { group: "Actions", label: "Switch to founder view",  act: ()=>{ setRole("founder"); setRoute("results"); } },
    { group: "Actions", label: "Switch to investor view", act: ()=>{ setRole("investor"); setRoute("results"); } },
    { group: "Actions", label: "Start guided demo",       act: ()=>setDemoOn(true) },
    { group: "Actions", label: "View IAS methodology",    act: ()=>window.showIASLegend?.() },
  ];

  const filt = q
    ? items.filter(i => i.label.toLowerCase().includes(q.toLowerCase()) || i.group.toLowerCase().includes(q.toLowerCase()))
    : items;

  React.useEffect(()=>{ setSel(0); }, [q]);

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSel(s => Math.min(filt.length-1, s+1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel(s => Math.max(0, s-1)); }
    else if (e.key === "Enter") { e.preventDefault(); const hit = filt[sel]; if (hit) { hit.act(); onClose(); } }
  };

  // group items for rendering
  const grouped = [];
  let lastGroup = null;
  filt.forEach((it, i) => {
    if (it.group !== lastGroup) { grouped.push({ kind:"group", label: it.group }); lastGroup = it.group; }
    grouped.push({ kind:"item", ...it, _idx: i });
  });

  return (
    <div className="cmdk-backdrop" onClick={onClose}>
      <div className="cmdk" onClick={(e)=>e.stopPropagation()}>
        <input
          className="cmdk-input"
          placeholder="Jump to anywhere — company, page, action…"
          autoFocus
          value={q}
          onChange={(e)=>setQ(e.target.value)}
          onKeyDown={onKey}
        />
        <div className="cmdk-list">
          {grouped.map((row,i)=>(
            row.kind==="group" ? (
              <div key={"g"+i} className="cmdk-group">{row.label}</div>
            ) : (
              <div key={"i"+row._idx}
                   className={"cmdk-item" + (row._idx===sel ? " on" : "")}
                   onMouseEnter={()=>setSel(row._idx)}
                   onClick={()=>{ row.act(); onClose(); }}>
                <span>{row.label}</span>
                {row.kb && <span className="eyebrow">{row.kb}</span>}
              </div>
            )
          ))}
          {filt.length === 0 && <div className="cmdk-item dim">No matches</div>}
        </div>
      </div>
    </div>
  );
}

export function IASLegend({ onClose }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e)=>e.stopPropagation()}>
        <div className="row" style={{marginBottom:6}}>
          <div className="h-eyebrow">Methodology</div>
          <div className="spacer"/>
          <button className="btn btn--quiet" onClick={onClose}>Close</button>
        </div>
        <h2 className="h-display" style={{fontSize:"var(--text-3xl)", marginBottom:16, lineHeight:1.1}}>
          How the <em>Investment Attractiveness Score</em> works.
        </h2>
        <p style={{fontSize:14, lineHeight:1.7, color:"var(--text-mute)", marginBottom:20}}>
          IAS is a weighted composite across seven axes, benchmarked to cohort (modality × stage). It is a triage signal, not a recommendation — the number exists to focus partner attention, never to replace judgment.
        </p>

        <div className="h-eyebrow" style={{marginBottom:10}}>Bands</div>
        <table className="t" style={{marginBottom:20}}>
          <thead>
            <tr><th>Score</th><th>Band</th><th>Typical action</th></tr>
          </thead>
          <tbody>
            <tr><td className="num">85–100</td><td>Top decile</td><td>Partner review within 48h</td></tr>
            <tr><td className="num">75–84</td><td>Strong</td><td>Analyst deep-dive; shortlist</td></tr>
            <tr><td className="num">60–74</td><td>Watch</td><td>Follow; re-score in 90 days</td></tr>
            <tr><td className="num">&lt; 60</td><td>Pass</td><td>Archive with rationale</td></tr>
          </tbody>
        </table>

        <div className="h-eyebrow" style={{marginBottom:10}}>Axis weights</div>
        <div style={{marginBottom:18}}>
          {MOCK.iasBreakdown.map(b=>(
            <div key={b.key} style={{display:"grid", gridTemplateColumns:"120px 1fr 50px", gap:12, padding:"6px 0", fontSize:13, alignItems:"center"}}>
              <div>{b.key}</div>
              <div style={{height:4, background:"var(--line)", borderRadius:2, overflow:"hidden"}}>
                <div style={{width:(b.weight*300)+"px", maxWidth:"100%", height:"100%", background:"var(--accent-hi)"}}/>
              </div>
              <div className="num dim" style={{textAlign:"right", fontSize:12}}>{(b.weight*100).toFixed(0)}%</div>
            </div>
          ))}
        </div>

        <div className="h-eyebrow" style={{marginBottom:10}}>Calibration</div>
        <p style={{fontSize:13, lineHeight:1.7, color:"var(--text-mute)"}}>
          Weights are reviewed quarterly by the investment committee against realized outcomes (TVPI of evaluated deals at 24-month checkpoint). Cohort medians are drawn from the trailing twelve months of Venzyme-evaluated decks at matching modality and stage. Extraction confidence below 0.70 suppresses the score; the record surfaces a "needs human review" state instead.
        </p>
      </div>
    </div>
  );
}

export function DemoBanner({ step, setStep, setRoute, setRole, setDashVariant, onExit }) {
  const steps = [
    { label: "Welcome — a guided walkthrough of one deck across the system.", act: ()=>setRoute("landing") },
    { label: "Step 1 · Landing. This is how a prospective fund encounters Venzyme.", act: ()=>setRoute("landing") },
    { label: "Step 2 · Dashboard. Claudia lands here each morning.", act: ()=>{ setRoute("dashboard"); setDashVariant("desk"); } },
    { label: "Step 3 · New analysis. She uploads a Helix deck.", act: ()=>setRoute("analyzer") },
    { label: "Step 4 · Pipeline. Helix appears in Shortlist, auto-routed.", act: ()=>setRoute("pipeline") },
    { label: "Step 5 · Analysis view. The investor sees the thesis, evidence, peers.", act: ()=>{ setRoute("results"); setRole("investor"); } },
    { label: "Step 6 · Founder view. Same record, coaching lens.", act: ()=>{ setRoute("results"); setRole("founder"); } },
    { label: "Step 7 · Admin. Ops see the fund-wide view.", act: ()=>setRoute("admin") },
    { label: "End of tour. Click around — everything is live.", act: ()=>{} },
  ];
  const cur = steps[Math.min(step, steps.length-1)];

  React.useEffect(()=>{ cur.act?.(); }, [step]);

  return (
    <div className="demo-banner">
      <span>● DEMO MODE</span>
      <span style={{letterSpacing:".04em", textTransform:"none", fontFamily:"var(--ff-body)"}}>{cur.label}</span>
      <span style={{flex:1}}/>
      <button onClick={()=>setStep(s => Math.max(0, s-1))} disabled={step===0}>← Back</button>
      <button onClick={()=>setStep(s => Math.min(steps.length-1, s+1))} disabled={step===steps.length-1}>Next →</button>
      <button onClick={onExit}>Exit</button>
    </div>
  );
}
